import { yupResolver } from '@hookform/resolvers/yup';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { animateScroll as scroll } from 'react-scroll/modules';

import { Checkbox } from '@eika/checkbox';
import { CurrencyInput } from '@eika/currency-input';
import { Input } from '@eika/input';
import { Select } from '@eika/select';

import { FormTypeApplicationMainApplicant } from '../../../actions';
import { DispatchWithState, StringKeyAnyValue, StringKeyValue } from '../../../actions/types';
import ErrorMessage from '../../../components/Form/ErrorMessage';
import Loader from '../../../components/Loader/Loader';
import StepButtons from '../../../components/Steps/StepButtons';
import { ApplicationReducer } from '../../../reducers/application';
import { CalculatorReducer } from '../../../reducers/calculator';
import { LoaderReducer } from '../../../reducers/loader';
import loanType from '../../../util/mappings/loanType';
import { mainApplicantSchema } from '../../../validation/loanApplicationSchema';

interface Props {
    action: {
        fetchCalculatorCache: () => DispatchWithState;
        loanApplicationMainApplicantNextEvent: (form: FormTypeApplicationMainApplicant) => DispatchWithState;
        openStep: (step: number) => void;
    };
    application: ApplicationReducer;
    form: StringKeyAnyValue;
    loader: LoaderReducer;
    text: StringKeyValue;
    calculator: CalculatorReducer;
}

const LoanApplicantMain = (props: Props): JSX.Element => {
    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    const { action, application, loader, text, form, calculator } = props;
    const { loanApplicationMainApplicantNextEvent, openStep } = action;
    const { mainApplicant } = application;
    const { firstName, rentalFee, totalDebt, tradeIn, tradeInDebt } = mainApplicant;
    const { isFetchingKyc, hasFetchedUserContext } = loader;
    const { fetchCalculatorCache } = action;

    useEffect(() => {
        fetchCalculatorCache();
    }, [fetchCalculatorCache]);

    const {
        handleSubmit,
        register,
        formState: { errors },
        watch,
    } = useForm<FormTypeApplicationMainApplicant>({
        resolver: yupResolver(mainApplicantSchema),
        shouldUnregister: true,
        mode: 'all',
        defaultValues: {
            ...mainApplicant,
        },
    });

    const hasError = Object.keys(errors).length > 0;
    const selectedLivingConditionCode = watch().livingConditionCode;
    const selectedTradeIn = watch().tradeIn;
    const formError = <ErrorMessage message={text.formErrorMessage} />;

    if (!hasFetchedUserContext) return <Loader title={text.loadingDefault} />;
    if (isFetchingKyc) return <Loader title={text.loadingKyc} />;

    return (
        <div className="main-applicant">
            {firstName && (
                <div className="main-applicant__greeting">
                    <h2 className="h2">Hei, {firstName}</h2>
                </div>
            )}

            <form
                onSubmit={handleSubmit((form) => {
                    loanApplicationMainApplicantNextEvent(form);
                })}
            >
                <div className="main-applicant__email input-wrapper">
                    <Input
                        {...register('email')}
                        type="email"
                        label={text.applicantLabelEmail}
                        error={errors?.email?.message || ''}
                    />
                </div>

                <div className="main-applicant__phone input-wrapper">
                    <Input
                        {...register('phone')}
                        type="tel"
                        label={text.applicantLabelPhone}
                        error={errors?.phone?.message || ''}
                        maxLength={8}
                    />
                </div>

                <div className="main-applicant__living-condition input-wrapper">
                    <Select
                        {...register('livingConditionCode')}
                        label={text.applicantLabelLivingCondition}
                        values={form.livingConditionCode.values}
                        error={errors?.livingConditionCode?.message || ''}
                        id="mainApplicantLiving"
                    />
                </div>

                {selectedLivingConditionCode === 'RENTS_HOUSING' && (
                    <div className="main-applicant__rental-fee input-wrapper">
                        <CurrencyInput
                            {...register('rentalFee')}
                            label={text.applicantLabelRentalFee}
                            placeholder={text.currencyInputPlaceholderKr}
                            defaultValue={rentalFee}
                            error={String(errors?.rentalFee?.message || '')}
                            id="mainApplicantRental"
                        />
                    </div>
                )}

                <div className="main-applicant__employment input-wrapper">
                    <Select
                        {...register('employmentConditionCode')}
                        label={text.applicantLabelEmployment}
                        values={form.employmentConditionCode.values}
                        error={errors?.employmentConditionCode?.message || ''}
                        id="mainApplicantEmployment"
                    />
                </div>

                <div className="main-applicant__debt input-wrapper">
                    <CurrencyInput
                        {...register('totalDebt')}
                        label={text.applicantMainTotalDebtLabel}
                        sublabel={text.applicantMainTotalDebtSubLabel}
                        placeholder={text.currencyInputPlaceholderKr}
                        defaultValue={totalDebt}
                        error={String(errors?.totalDebt?.message || '')}
                        max={1000000000}
                        id="mainApplicantDebt"
                    />
                </div>

                {text.applicantTitleTradeIn && (
                    <h3
                        style={{
                            visibility: calculator.loanInfo.loanType === loanType.loanCommitment ? 'hidden' : 'visible',
                        }}
                    >
                        {text.applicantTitleTradeIn}
                    </h3>
                )}

                <div
                    className="main-applicant__trade-in input-wrapper"
                    style={{
                        visibility: calculator.loanInfo.loanType === loanType.loanCommitment ? 'hidden' : 'visible',
                    }}
                >
                    <Checkbox
                        {...register('tradeIn')}
                        label={text.applicantLabelTradeIn}
                        defaultChecked={tradeIn}
                        error={errors?.tradeIn?.message || ''}
                        id="mainApplicantTradeIn"
                    />
                </div>

                {selectedTradeIn && (
                    <div className="main-applicant__trade-in-debt input-wrapper">
                        <CurrencyInput
                            {...register('tradeInDebt', {
                                valueAsNumber: true,
                            })}
                            label={text.applicantLabelTradeInDebt}
                            placeholder={text.currencyInputPlaceholderKr}
                            defaultValue={tradeInDebt}
                            error={String(errors?.tradeInDebt?.message || '')}
                            id="mainApplicantTradeInDebt"
                        />
                    </div>
                )}

                {hasError && formError}

                <StepButtons showPrevious previousChangeEvent={() => openStep(1)} nextButtonType="submit" />
            </form>
        </div>
    );
};

export default LoanApplicantMain;
